// The landing page (PRD 012 §6 / increment 0) — the funnel. Backend-free: a
// scripted session simulation (LandingSim) is the hero, beside the brand pitch
// and a single CTA; below, the four-beat "how it works"; then a warm-ink depth
// band closing CTA. Visual language per docs/design-system.md (Fraunces display,
// the clay spark accent, the new --depth field).

import {
  AudioLines,
  Mic,
  PenLine,
  ScrollText,
  Settings as SettingsIcon,
  Sparkles,
  type LucideIcon
} from 'lucide-react'
import { LandingSim } from '../components/landing/LandingSim'
import { Button } from '../components/ui/Button'

interface Beat {
  Icon: LucideIcon
  title: string
  body: string
}

const BEATS: Beat[] = [
  {
    Icon: AudioLines,
    title: 'Talk',
    body: 'A team speaks naturally. Each voice is captured and diarized — no note-taker, no “okay, who’s minuting this?”'
  },
  {
    Icon: ScrollText,
    title: 'Live transcript',
    body: 'Speech becomes a clean, attributed transcript as you go — the shared record of the room, in real time.'
  },
  {
    Icon: Sparkles,
    title: 'The AI participates',
    body: 'A listening AI surfaces ideas, connections and cautions inline — and answers when the room calls it by name.'
  },
  {
    Icon: PenLine,
    title: 'Shared whiteboard',
    body: 'Durable points get pinned to a collaborative canvas the AI co-edits, so the thinking outlives the conversation.'
  }
]

export function Home() {
  return (
    <div className="w-full">
      {/* Hero */}
      <section className="mx-auto grid w-full max-w-6xl items-center gap-10 px-4 py-12 md:px-6 lg:grid-cols-2 lg:gap-12 lg:py-16">
        <div className="max-w-xl">
          <p className="flex items-center gap-1.5 text-body-sm font-semibold tracking-wide text-accent uppercase">
            <Sparkles className="size-3.5" />
            convariance
          </p>
          <h1 className="mt-3 font-serif text-display-l text-text lg:text-display-xl">
            A thinking partner that follows your{' '}
            <span className="text-accent italic">conversation</span>, live.
          </h1>
          <p className="mt-5 text-body-l text-muted-foreground">
            A team talks; convariance transcribes the room as you speak, and an
            AI participant listens, thinks, and contributes inline — then builds
            a shared whiteboard of everything you decide.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a href="#/session">
              <Button size="lg">
                <Mic /> Start a session
              </Button>
            </a>
            <a href="#/settings">
              <Button size="lg" variant="outline">
                <SettingsIcon /> Settings
              </Button>
            </a>
          </div>
          <p className="mt-4 text-body-sm text-muted-foreground">
            Your own Claude Code is the AI. Bring your agent — your room stays
            yours.
          </p>
        </div>

        {/* The product is the demo: a live-looking session replaying on a loop. */}
        <div className="h-[480px] w-full lg:h-[520px]">
          <LandingSim />
        </div>
      </section>

      {/* How it works — four beats */}
      <section className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6">
        <h2 className="font-serif text-display-m text-text">How it works</h2>
        <p className="mt-2 text-body text-muted-foreground">
          Four beats, from the first word to a shared artifact.
        </p>
        <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {BEATS.map((beat, i) => (
            <li
              key={beat.title}
              className="rounded-xl border border-border bg-surface p-5 shadow-(--shadow-sm)"
            >
              <div className="flex items-center gap-2.5">
                <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-accent/10 text-accent">
                  <beat.Icon className="size-4.5" />
                </span>
                <span className="text-label font-semibold tracking-wide text-muted-foreground uppercase">
                  Step {i + 1}
                </span>
              </div>
              <h3 className="mt-3 font-serif text-display-m text-text">
                {beat.title}
              </h3>
              <p className="mt-1.5 text-body-sm text-muted-foreground">
                {beat.body}
              </p>
            </li>
          ))}
        </ol>
      </section>

      {/* Closing CTA on the warm-ink depth field (design-system §2). */}
      <section className="px-4 pb-12 md:px-6">
        <div className="mx-auto w-full max-w-6xl overflow-hidden rounded-2xl bg-depth px-6 py-12 text-center md:px-12 md:py-16">
          <h2 className="mx-auto max-w-2xl font-serif text-display-l text-depth-fg">
            Stop minuting. Start thinking out loud.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-body-l text-depth-muted">
            Spin up a room, talk through the hard problem, and let the AI keep
            the thread with you.
          </p>
          <div className="mt-8 flex justify-center">
            <a href="#/session">
              <Button size="lg">
                <Mic /> Start a session
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
